import React, { useEffect, useState } from 'react';
import { ClipboardPaste, Plus, Trash2 } from 'lucide-react';

import { Aide } from '../ui/aide';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { BarrePagination } from '../ui/pagination-bar';
import { TAILLE_PAGE_PAR_DEFAUT, paginer } from '../../hooks/usePagination';
import { cn } from '../../lib/utils';
import {
  analyserCollage,
  ligneSansPlaque,
  nouvelleLigneCamion,
  PRODUITS_CAMION,
  resumeGrille,
} from '../../lib/lotsCollage';
import { libelle } from '../../lib/libelles';

const COLONNES = [
  { cle: 'tractor_plate', titre: 'Tracteur', classe: 'min-w-[8rem] font-mono' },
  { cle: 'tank_plate', titre: 'Citerne', classe: 'min-w-[8rem] font-mono' },
  { cle: 'driver_phone', titre: 'Téléphone', classe: 'min-w-[8rem]', mode: 'tel' },
  { cle: 'driver_name', titre: 'Chauffeur', classe: 'min-w-[9rem]' },
  { cle: 'expected_quantity', titre: 'Quantité (L)', classe: 'min-w-[6rem] text-right tabular-nums', mode: 'decimal' },
  { cle: 'product', titre: 'Produit', classe: 'min-w-[7rem]', choix: PRODUITS_CAMION },
];

/**
 * Liste des camions d'un lot : lignes collées depuis Excel (tabulation ou
 * point-virgule, ou une simple colonne de plaques) ou saisies une à une.
 * Chaque ligne s'affiche aussitôt dans le tableau, corrigeable, retirable.
 *
 * Le composant ne connaît pas le lot : `lignes` et `onChange` suffisent. Les
 * lignes à envoyer se prennent avec `lignesPourEnvoi` (lib/lotsCollage.js).
 *
 * Quatre cents camions collés ne font pas un tableau sans fin : l'aperçu se lit
 * par pages de 20, 50 ou 100. Toutes les lignes restent dans la liste envoyée
 * (le résumé les compte toutes) ; seule la page courante est montrée. La page
 * est un état du formulaire, pas de l'adresse : ce tableau vit dans un dialogue.
 */
export const GrilleCamions = ({ lignes, onChange, disabled = false, className }) => {
  const [texte, setTexte] = useState('');
  const [numeroPage, setNumeroPage] = useState(1);
  const [taillePage, setTaillePage] = useState(TAILLE_PAGE_PAR_DEFAUT);
  const resume = resumeGrille(lignes);
  const tranche = paginer(lignes, numeroPage, taillePage);
  const pagination = {
    ...tranche,
    allerA: (cible) => setNumeroPage(Math.max(1, Number(cible) || 1)),
    changerTaille: (nouvelle) => {
      setTaillePage(nouvelle);
      setNumeroPage(1);
    },
  };

  // Liste vidée (ou formulaire remis à zéro) : la prochaine liste commence à la page 1.
  useEffect(() => {
    if (lignes.length === 0) setNumeroPage(1);
  }, [lignes.length]);

  const ajouter = (nouvelles, { versLaFin = false } = {}) => {
    if (nouvelles.length === 0) return;
    onChange([...lignes, ...nouvelles]);
    // Une ligne ajoutée à la main se saisit tout de suite : on va la chercher.
    if (versLaFin) setNumeroPage(Math.ceil((lignes.length + nouvelles.length) / taillePage));
  };

  // Un collage depuis Excel se lit à l'instant : les lignes arrivent dans le tableau.
  const surCollage = (evenement) => {
    const collé = evenement.clipboardData?.getData('text');
    if (!collé) return;
    evenement.preventDefault();
    ajouter(analyserCollage(collé));
    setTexte('');
  };

  const ajouterLeTexte = () => {
    ajouter(analyserCollage(texte));
    setTexte('');
  };

  const changer = (cle, colonne, valeur) =>
    onChange(lignes.map((ligne) => (ligne.cle === cle ? { ...ligne, [colonne]: valeur } : ligne)));

  const retirer = (cle) => onChange(lignes.filter((ligne) => ligne.cle !== cle));

  return (
    <div className={cn('space-y-3', className)} data-testid="grille-camions">
      <div className="space-y-2">
        <div className="flex items-center gap-1.5">
          <label htmlFor="grille-collage" className="text-sm font-medium text-foreground">
            Coller les camions
          </label>
          <Aide titre="Coller les camions">
            Copiez les lignes dans Excel puis collez-les ici. Colonnes dans cet ordre : tracteur, citerne,
            téléphone du chauffeur, nom du chauffeur, quantité, produit. Une simple colonne de plaques convient aussi.
          </Aide>
        </div>
        <Textarea
          id="grille-collage"
          value={texte}
          onChange={(evenement) => setTexte(evenement.target.value)}
          onPaste={surCollage}
          disabled={disabled}
          rows={3}
          spellCheck={false}
          className="font-mono text-sm"
          placeholder="Coller depuis Excel"
          data-testid="grille-collage"
        />
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={ajouterLeTexte}
            disabled={disabled || texte.trim() === ''}
            data-testid="grille-ajouter-texte"
          >
            <ClipboardPaste className="h-4 w-4" aria-hidden="true" />
            Ajouter à la liste
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => ajouter([nouvelleLigneCamion()], { versLaFin: true })}
            disabled={disabled}
            data-testid="grille-ajouter-ligne"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            Ajouter une ligne
          </Button>
        </div>
      </div>

      {lignes.length > 0 ? (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <span className="font-medium text-foreground" data-testid="grille-resume">
              {resume.total} camion{resume.total > 1 ? 's' : ''}
              {resume.sansPlaque > 0 ? (
                <span className="ml-2 font-normal text-destructive">
                  dont {resume.sansPlaque} sans plaque de tracteur
                </span>
              ) : null}
            </span>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => onChange([])}
              disabled={disabled}
              data-testid="grille-vider"
            >
              Vider la liste
            </Button>
          </div>
          <div className="max-h-72 overflow-auto rounded-lg border border-border">
            <table className="w-full text-sm" data-testid="grille-apercu">
              <thead className="sticky top-0 bg-muted/80 text-xs text-muted-foreground backdrop-blur">
                <tr>
                  {COLONNES.map((colonne) => (
                    <th
                      key={colonne.cle}
                      className={cn('px-2 py-1.5 text-left font-medium', colonne.cle === 'expected_quantity' && 'text-right')}
                      scope="col"
                    >
                      {colonne.titre}
                    </th>
                  ))}
                  <th className="w-10 px-2 py-1.5" scope="col">
                    <span className="sr-only">Retirer</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {tranche.lignes.map((ligne, position) => {
                  // Le rang est celui de la liste entière, pas celui de la page.
                  const index = tranche.premiere - 1 + position;
                  return (
                    <tr key={ligne.cle} data-testid={`grille-ligne-${index}`}>
                      {COLONNES.map((colonne) => (
                        <td key={colonne.cle} className="px-1.5 py-1">
                          {colonne.choix ? (
                            <select
                              value={ligne[colonne.cle] ?? ''}
                              onChange={(evenement) => changer(ligne.cle, colonne.cle, evenement.target.value)}
                              disabled={disabled}
                              aria-label={`${colonne.titre}, ligne ${index + 1}`}
                              className={cn(
                                'h-8 w-full rounded-md border border-input bg-background px-2 text-sm',
                                colonne.classe,
                              )}
                            >
                              <option value="">—</option>
                              {colonne.choix.map((code) => (
                                <option key={code} value={code}>
                                  {libelle('fuelProduct', code)}
                                </option>
                              ))}
                            </select>
                          ) : (
                          <Input
                            value={ligne[colonne.cle] ?? ''}
                            onChange={(evenement) => changer(ligne.cle, colonne.cle, evenement.target.value)}
                            inputMode={colonne.mode}
                            disabled={disabled}
                            aria-label={`${colonne.titre}, ligne ${index + 1}`}
                            aria-invalid={colonne.cle === 'tractor_plate' && ligneSansPlaque(ligne) ? true : undefined}
                            className={cn(
                              'h-8 px-2 text-sm',
                              colonne.classe,
                              colonne.cle === 'tractor_plate' && ligneSansPlaque(ligne) && 'border-destructive',
                            )}
                          />
                          )}
                        </td>
                      ))}
                      <td className="px-1.5 py-1 text-right">
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          onClick={() => retirer(ligne.cle)}
                          disabled={disabled}
                          aria-label={`Retirer la ligne ${index + 1}`}
                          data-testid={`grille-retirer-${index}`}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <BarrePagination pagination={pagination} nom="camions" />
        </div>
      ) : null}
    </div>
  );
};

export default GrilleCamions;
